import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  CheckIcon,
  XMarkIcon,
  DocumentTextIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { useAuthContext } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { userService, User } from '../services/userService';
import { businessService, BusinessListing } from '../services/businessService';
import { messageService, Conversation } from '../services/messageService';

const AdminUserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentUser, userProfile } = useAuthContext();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Calculated statistics
  const [userStats, setUserStats] = useState({
    listingsCount: 0,
    savedListingsCount: 0,
    messagesCount: 0
  });
  
  // User's conversations
  const [userConversations, setUserConversations] = useState<Conversation[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  
  // User's listings
  const [userListings, setUserListings] = useState<BusinessListing[]>([]);

  usePageTitle(user ? `User: ${user.profile.firstName} ${user.profile.lastName} | Admin` : 'User Details | Admin');

  const calculateUserStats = useCallback(async (userId: string) => {
    try {
      // Get user's listings
      const userListings = await businessService.getBusinessesByOwner(userId);
      setUserListings(userListings);
      
      // Get user's conversations
      const userConversations = await messageService.getConversationsForUser(userId);
      
      // Calculate total messages across all conversations
      let totalMessages = 0;
      userConversations.forEach(conversation => {
        totalMessages += conversation.messageCount || 0;
      });
      
      setUserStats({
        listingsCount: userListings.length,
        savedListingsCount: user?.stats?.savedListingsCount || 0, // This would need a separate service call
        messagesCount: totalMessages
      });
    } catch (error) {
      console.error('Error calculating user stats:', error);
    }
  }, [user?.stats?.savedListingsCount]);

  useEffect(() => {
    const loadUser = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const userData = await userService.getUser(id);
        if (userData) {
          setUser(userData);
          
          // Calculate real-time statistics
          await calculateUserStats(userData.id!);
          
          // Load user conversations
          await loadUserConversations(userData.id!);
        } else {
          setError('User not found');
        }
      } catch (error) {
        console.error('Error loading user:', error);
        setError('Failed to load user');
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, [id, calculateUserStats]);

  const loadUserConversations = async (userId: string) => {
    try {
      setConversationsLoading(true);
      const conversations = await messageService.getConversationsForUser(userId);
      setUserConversations(conversations);
    } catch (error) {
      console.error('Error loading user conversations:', error);
    } finally {
      setConversationsLoading(false);
    }
  };

  // Check if user is admin
  const isAdmin = currentUser && userProfile?.profile.userType === 'admin';

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">⚠️ Access Denied</div>
          <p className="text-gray-600 mb-4">You don't have permission to access the admin area.</p>
          <Link to="/" className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700">
            Go Home
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading user details...</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">❌ Error</div>
          <p className="text-gray-600 mb-4">{error || 'User not found'}</p>
          <Link to="/admin/users" className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700">
            Back to Users
          </Link>
        </div>
      </div>
    );
  }

  const getStatusColor = (userType: string) => {
    switch (userType) {
      case 'buyer': return 'bg-blue-100 text-blue-800';
      case 'seller': return 'bg-green-100 text-green-800';
      case 'broker': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      <Helmet>
        <title>User: {user.profile.firstName} {user.profile.lastName} | Admin</title>
        <meta name="description" content="User details in admin dashboard" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Link
                  to="/admin/users"
                  className="flex items-center text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeftIcon className="h-5 w-5 mr-2" />
                  Back to Users
                </Link>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">User Details</h1>
                  <p className="text-gray-600 mt-1">View user information and activity</p>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <Link
                  to={`/admin/users/${user.id}/edit`}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                  Edit User
                </Link>
                <Link
                  to="/admin/dashboard"
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                  Back to Dashboard
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* User Profile Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                    <UserIcon className="h-8 w-8 text-primary-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {user.profile.firstName} {user.profile.lastName}
                    </h2>
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(user.profile.userType)}`}>
                      {user.profile.userType.charAt(0).toUpperCase() + user.profile.userType.slice(1)}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-900">{user.profile.email}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <PhoneIcon className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-900">{user.profile.phone}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <MapPinIcon className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-900">
                          {user.profile.location.city}, {user.profile.location.province}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <CalendarIcon className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-900">
                          Joined: {user.profile.createdAt?.toDate().toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <CalendarIcon className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-900">
                          Last Active: {user.profile.lastActive?.toDate().toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Verification Status */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Verification Status</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    {user.profile.verification.emailVerified ? (
                      <CheckIcon className="h-5 w-5 text-green-600" />
                    ) : (
                      <XMarkIcon className="h-5 w-5 text-red-600" />
                    )}
                    <span className={user.profile.verification.emailVerified ? 'text-green-800' : 'text-red-800'}>
                      Email Verified
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    {user.profile.verification.phoneVerified ? (
                      <CheckIcon className="h-5 w-5 text-green-600" />
                    ) : (
                      <XMarkIcon className="h-5 w-5 text-red-600" />
                    )}
                    <span className={user.profile.verification.phoneVerified ? 'text-green-800' : 'text-red-800'}>
                      Phone Verified
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    {user.profile.verification.identityVerified ? (
                      <CheckIcon className="h-5 w-5 text-green-600" />
                    ) : (
                      <XMarkIcon className="h-5 w-5 text-red-600" />
                    )}
                    <span className={user.profile.verification.identityVerified ? 'text-green-800' : 'text-red-800'}>
                      Identity Verified
                    </span>
                  </div>
                </div>
                {user.profile.verification.brokerLicense && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <span className="text-blue-800 font-medium">Broker License: {user.profile.verification.brokerLicense}</span>
                  </div>
                )}
              </div>

              {/* User Stats */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">User Statistics</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-primary-50 rounded-lg">
                    <p className="text-2xl font-bold text-primary-800">{userStats.listingsCount}</p>
                    <p className="text-sm text-gray-600">Listings</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-800">{userStats.savedListingsCount}</p>
                    <p className="text-sm text-gray-600">Saved Listings</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-800">{userStats.messagesCount}</p>
                    <p className="text-sm text-gray-600">Messages</p>
                  </div>
                </div>
              </div>

              {/* User Messages */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">User Messages</h3>
                {conversationsLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading conversations...</p>
                  </div>
                ) : userConversations.length > 0 ? (
                  <div className="space-y-4">
                    {userConversations.map((conversation) => (
                      <div key={conversation.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                              <DocumentTextIcon className="h-5 w-5 text-primary-600" />
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">
                                {conversation.businessTitle || 'Business Conversation'}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {conversation.messageCount || 0} messages • {conversation.participants.length} participants
                              </p>
                              {conversation.lastMessage && (
                                <p className="text-sm text-gray-500 mt-1">
                                  Last: {conversation.lastMessage.content.substring(0, 50)}...
                                </p>
                              )}
                            </div>
                          </div>
                          <Link
                            to={`/admin/conversations/${conversation.id}`}
                            className="text-primary-600 hover:text-primary-900 p-2"
                            title="View Conversation"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No messages found for this user.</p>
                  </div>
                )}
              </div>

              {/* User Listings */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">User Listings</h3>
                {userListings.length > 0 ? (
                  <div className="space-y-4">
                    {userListings.map((listing) => (
                      <div key={listing.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                              <DocumentTextIcon className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">
                                {listing.basic.title}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {listing.basic.category} • {listing.basic.status} • ${listing.financial.askingPrice}
                              </p>
                              <p className="text-sm text-gray-500 mt-1">
                                {listing.location.city}, {listing.location.province}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Link
                              to={listing.basic.seoUrl ? `/business/${listing.basic.seoUrl}` : `/business/${listing.id}`}
                              className="text-primary-600 hover:text-primary-900 p-2"
                              title="View Listing"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Link>
                            <Link
                              to={`/admin/listings/${listing.id}/edit`}
                              className="text-gray-600 hover:text-gray-900 p-2"
                              title="Edit Listing"
                            >
                              <DocumentTextIcon className="h-4 w-4" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No listings found for this user.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Quick Actions */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <Link
                    to={`/admin/users/${user.id}/edit`}
                    className="w-full bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md font-medium transition-colors text-center block"
                  >
                    Edit User
                  </Link>
                  <button className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium transition-colors">
                    Deactivate User
                  </button>
                </div>
              </div>

              {/* User Preferences */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Preferences</h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Categories:</span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {user.profile.preferences.categories.map((category, index) => (
                        <span key={index} className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">
                          {category}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">Price Range:</span>
                    <p className="text-sm text-gray-600">
                      ${user.profile.preferences.priceRange.min.toLocaleString()} - ${user.profile.preferences.priceRange.max.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">Email Notifications:</span>
                    <p className="text-sm text-gray-600">
                      {user.profile.preferences.emailNotifications ? 'Enabled' : 'Disabled'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminUserDetail; 